/**
 * Meta Pixel (Facebook Pixel) Integration
 * 
 * Provides browser-side tracking for Meta advertising platform.
 * Works in conjunction with server-side Meta Conversion API for deduplication.
 * 
 * @see packages/backend/src/services/meta-capi.ts for server-side tracking
 */

declare global {
  interface Window {
    fbq?: any;
    _fbq?: any;
  }
}

/**
 * Initialize Meta Pixel
 * 
 * @param pixelId - Your Meta Pixel ID from Meta Events Manager
 */
export const initMetaPixel = (pixelId: string) => {
  if (typeof window === 'undefined') return;
  
  // Check if fbq already exists
  if (window.fbq) return;

  // Create fbq function
  const fbq: any = function(...args: any[]) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args);
    } else {
      fbq.queue.push(args);
    }
  };
  
  if (!window.fbq) window.fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  
  // Load pixel script
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  
  // Add noscript pixel
  const noscript = document.createElement('noscript');
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);
  
  // Initialize pixel
  fbq('init', pixelId);
  fbq('track', 'PageView');
};

/**
 * Track standard Meta event
 * 
 * Standard events: PageView, ViewContent, Search, AddToCart, AddToWishlist,
 * InitiateCheckout, AddPaymentInfo, Purchase, Lead, CompleteRegistration
 * 
 * @param eventName - Standard event name
 * @param params - Event parameters
 */
export const trackEvent = (eventName: string, params?: Record<string, any>) => {
  if (typeof window === 'undefined' || !window.fbq) return;
  
  // Extract event_id into the options object for proper deduplication with server-side CAPI
  const { event_id, ...eventParams } = params || {};
  if (event_id) {
    window.fbq('track', eventName, eventParams, { eventID: event_id });
  } else {
    window.fbq('track', eventName, eventParams);
  }
};

/**
 * Track custom Meta event
 * 
 * @param eventName - Custom event name
 * @param params - Event parameters
 */
export const trackCustomEvent = (eventName: string, params?: Record<string, any>) => {
  if (typeof window === 'undefined' || !window.fbq) return;
  
  window.fbq('trackCustom', eventName, params);
};

/**
 * Track page view (useful for SPA navigation)
 */
export const trackPageView = () => {
  if (typeof window === 'undefined' || !window.fbq) return;
  
  window.fbq('track', 'PageView');
};

// ── Facebook tracking param persistence (localStorage) ──

const FB_TRACKING_KEY = 'fb_tracking';
const FB_TRACKING_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface FbTracking {
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  savedAt: number;
}

// Snapshot fbclid from the very first URL before any script can strip it.
// Facebook's pixel script calls history.replaceState to remove fbclid from
// the address bar, so we must capture it synchronously at module load time.
export const initialFbclid: string | undefined = (() => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('fbclid') || undefined;
  } catch {
    return undefined;
  }
})();

/**
 * Save Facebook tracking params to localStorage.
 * Uses the fbclid snapshot captured at module load (before the pixel can strip it),
 * reads _fbc cookie (set by pixel on ad click — more reliable than fbclid URL param),
 * and reads _fbp from cookie.
 * Call this on every page load so params survive the Hyp redirect round-trip.
 */
export const saveFbTracking = () => {
  if (typeof window === 'undefined') return;

  try {
    const fbclid = initialFbclid;
    const fbcMatch = document.cookie.match(/_fbc=([^;]+)/);
    const fbpMatch = document.cookie.match(/_fbp=([^;]+)/);
    const fbc = fbcMatch ? fbcMatch[1] : undefined;
    const fbp = fbpMatch ? fbpMatch[1] : undefined;

    // Only write if we have something new
    if (!fbclid && !fbc && !fbp) return;

    const existing = getFbTracking();
    const updated: FbTracking = {
      fbclid: fbclid || existing.fbclid,
      fbc: fbc || existing.fbc,
      fbp: fbp || existing.fbp,
      savedAt: Date.now(),
    };

    localStorage.setItem(FB_TRACKING_KEY, JSON.stringify(updated));
  } catch {
    // localStorage may be unavailable — silently ignore
  }
};

/**
 * Retrieve stored Facebook tracking params.
 * Returns empty values if expired or missing.
 */
export const getFbTracking = (): { fbclid?: string; fbc?: string; fbp?: string } => {
  if (typeof window === 'undefined') return {};

  try {
    const raw = localStorage.getItem(FB_TRACKING_KEY);
    if (!raw) return {};

    const data: FbTracking = JSON.parse(raw);
    if (Date.now() - data.savedAt > FB_TRACKING_TTL_MS) {
      localStorage.removeItem(FB_TRACKING_KEY);
      return {};
    }

    return { fbclid: data.fbclid, fbc: data.fbc, fbp: data.fbp };
  } catch {
    return {};
  }
};
